"use client";
import {
  InputOnChangeData,
  makeStyles,
  SearchBox,
  SearchBoxChangeEvent,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TabList,
  tokens,
} from "@fluentui/react-components";
import { useSearchParams } from "react-router-dom";
import { useTagCollectionsGet } from "../hooks/useTagCollections";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { TagCollectionEditor } from "../components/tagcollectioneditor/TagCollectionEditor";
import { SelectedTagProvider } from "../contexts/SelectedTagContext";
import { useTagSearch, SearchResult } from "../hooks/useTagSearch";
import { TagSearchResults } from "../components/tagcollectioneditor/TagSearchResults";
import { TagCollectionDataLoader } from "../components/tagcollectioneditor/TagCollectionDataLoader";
import { FlatVsaaTag } from "../models/VsaaTag";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  searchBox: {
    marginBottom: tokens.spacingVerticalM,
    width: "300px",
  },
  tabs: {
    paddingBottom: tokens.spacingVerticalL,
  },
  tabList: {
    flexWrap: "wrap",
  },
});

const TagManagerPage = () => {
  const classes = useClasses();
  const { data: tagCollections, isLoading } = useTagCollectionsGet();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [pendingSelectedTagId, setPendingSelectedTagId] = useState<
    string | undefined
  >(undefined);
  const [allTagsMap, setAllTagsMap] = useState<Map<string, FlatVsaaTag[]>>(
    new Map()
  );

  const handleTagData = useCallback(
    (id: string, data: FlatVsaaTag[] | undefined) => {
      if (data) {
        setAllTagsMap((prev) => {
          const next = new Map(prev);
          next.set(id, data);
          return next;
        });
      }
    },
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const viewFromParam: string | null = searchParams.get("view");

  const [view, setView] = useState<string | undefined>(() => {
    const chosenView = viewFromParam;

    if (chosenView) {
      return chosenView;
    }
  });

  const resolvedCollections = tagCollections ?? [];
  const isSearchLoading =
    debouncedSearchTerm.length > 0 &&
    resolvedCollections.some((c) => !allTagsMap.has(c.id));

  const { results: searchResults } = useTagSearch(
    debouncedSearchTerm,
    allTagsMap,
    resolvedCollections
  );

  const onTabSelect = (
    _event: SelectTabEvent<HTMLElement>,
    data: SelectTabData
  ) => {
    const value = data.value as string;

    searchParams.set("view", value);

    setSearchParams(searchParams, {
      preventScrollReset: true,
    });

    setView(value);
  };

  const onSearchChange = (
    _event: SearchBoxChangeEvent,
    data: InputOnChangeData
  ) => {
    setSearchTerm(data.value);
  };

  const onSelectSearchResult = (result: SearchResult) => {
    setSearchTerm("");
    setPendingSelectedTagId(result.id);
    const collectionId = result.tagCollectionId;
    searchParams.set("view", collectionId);
    setSearchParams(searchParams, { preventScrollReset: true });
    setView(collectionId);
  };

  if (!tagCollections || isLoading) {
    return <Spinner>{t("loadingTagCollections")}</Spinner>;
  }

  // If there is no view specified in the route set the first tag collection as visible.
  const targetView = view ? view : tagCollections[0].id;

  return (
    <div className={classes.root}>
      {debouncedSearchTerm.length > 0 &&
        resolvedCollections.map((c) => (
          <TagCollectionDataLoader
            key={c.id}
            collectionId={c.id}
            onData={handleTagData}
          />
        ))}
      <h1>{t("tagManager")}</h1>
      <SearchBox
        className={classes.searchBox}
        placeholder={t("searchTag")}
        value={searchTerm}
        onChange={onSearchChange}
      />
      {searchTerm.length === 0 ? (
        <>
          <div className={classes.tabs}>
            <TabList
              size="large"
              selectedValue={targetView}
              onTabSelect={onTabSelect}
              className={classes.tabList}
            >
              {tagCollections.map((tagCollection) => (
                <Tab key={tagCollection.id} value={tagCollection.id}>
                  {tagCollection.title}
                </Tab>
              ))}
            </TabList>
          </div>

          {targetView && (
            <SelectedTagProvider key={targetView}>
              <TagCollectionEditor
                tagCollectionId={targetView}
                initialSelectedTagId={pendingSelectedTagId}
                onInitialTagSelected={() => setPendingSelectedTagId(undefined)}
              ></TagCollectionEditor>
            </SelectedTagProvider>
          )}
        </>
      ) : (
        <TagSearchResults
          results={searchResults}
          searchTerm={searchTerm}
          isLoading={isSearchLoading || searchTerm !== debouncedSearchTerm}
          onSelectResult={onSelectSearchResult}
        />
      )}
    </div>
  );
};

export default TagManagerPage;
